// Social Engage Today: one Instagram task at a time for reviewers (coaches/teachers) and admins.
// A person does every action in Instagram and taps Done or Skip; no bulk anything. Pacing and
// caps are enforced by the server (/api/engage/today). The page only follows the server: it
// counts down the server's wait_seconds and loads the next task at zero (David, 2026-10-15,
// relaxing the original "no auto-advance"), and quietly checks again every 30 s while the
// tasks are being prepared or not open yet.
// Visible text says "task", never "card" or "batch" (server details are reworded below).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './EngageTodayPage.css';

const REUSED = 'That wording was used in the last week. Please change it.';
const POLL_MS = 30000;

// No stored name (school, team, booster, AD accounts): say what the account is, never the bare handle.
const ACCOUNT_KIND = { program: 'Athletic department account', team: 'Team account',
  booster: 'Booster club account', ad: 'Athletic director account' };

const schoolLine = (s) => {
  if (!s || !s.name) return '';
  const place = [s.town, s.state].filter(Boolean).join(', ');
  return [s.mascot ? `${s.name} ${s.mascot}` : s.name, place].filter(Boolean).join(' · ');
};

const accountName = (c) => {
  if (c.label) return c.label;
  const kind = ACCOUNT_KIND[c.role] || 'Account';
  return c.school && c.school.name ? `${kind} for ${c.school.name}` : kind;
};

const clock = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

// Server details are written for the API ("This card is already finished."); show them as tasks.
const plain = (s) => String(s).replace(/\b(card)(s?)\b/gi, (_m, w, pl) => (w[0] === 'C' ? 'Task' : 'task') + pl);

export default function EngageTodayPage() {
  const { API_URL } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState(0);   // seconds left in the server's follow gap
  const [wait, setWait] = useState(null);          // { seconds } from the latest load, new object each time
  const mounted = useRef(true);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const load = useCallback(() => {
    setError('');
    return axios.get(`${API_URL}/engage/today`).then((r) => {
      if (!mounted.current) return;
      setData(r.data);
      setCopied(false);
      const w = !r.data.card && r.data.wait_seconds > 0 ? Math.ceil(r.data.wait_seconds) : 0;
      setRemaining(w);
      setWait(w ? { seconds: w } : null);
      const d = r.data.card && r.data.card.drafts;
      setText(d && d.length ? d[0] : '');
    }).catch(() => { if (mounted.current) setError("Could not load today's list. Try again."); });
  }, [API_URL]);

  useEffect(() => { load(); }, [load]);

  // Countdown from the server's wait_seconds, one step a second; at zero, ask the server again.
  // Each load with a wait starts a fresh countdown (even the same number), and every wait is
  // at least 1 s, so fetches are always at least 1 s apart.
  useEffect(() => {
    if (!wait) return undefined;
    let left = wait.seconds;
    const t = setInterval(() => {
      left -= 1;
      if (left > 0) { setRemaining(left); return; }
      clearInterval(t);
      setRemaining(0);
      load();
    }, 1000);
    return () => clearInterval(t);
  }, [wait, load]);

  // Quiet refresh every 30 s while nothing is open yet (being prepared, morning or afternoon
  // not open), or if a countdown's fetch failed. Stops once a task shows or the day is over.
  const c0 = data && data.card;
  const polling = Boolean(data && !c0 && (data.wait_seconds > 0 ? remaining === 0 : (data.opens_at || data.built === false)));
  useEffect(() => {
    if (!polling) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [polling, load]);

  const finish = (result) => {
    if (result === 'stop' && !window.confirm('Mark this account as "asked us to stop or blocked us"? We will never contact it again.')) return;
    const isComment = data.card.kind === 'comment';
    const body = { result };
    if (isComment && result === 'done') body.comment_text = text;
    setBusy(true); setError('');
    axios.post(`${API_URL}/engage/today/cards/${data.card.id}`, body)
      .then(load)
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 409 && isComment && result === 'done' && /used in the last/i.test(e?.response?.data?.detail || '')) {
          setError(REUSED);   // stay on this task; the text box keeps the wording to change
          return undefined;
        }
        const msg = e?.response?.data?.detail ? plain(e.response.data.detail) : 'That did not save. Try again.';
        // 409 means this task is finished or gone; 404 means it is not today's any more (a Done
        // tapped after midnight Central). Either way: show the next one, keep the message.
        if (status === 409 || status === 404) return load().then(() => setError(msg));
        setError(msg);
        return undefined;
      })
      .finally(() => setBusy(false));
  };

  const writeClipboard = (value) => {
    if (!navigator.clipboard) {
      setError('Could not copy. Press and hold the text to copy it.');
      return Promise.resolve();
    }
    return navigator.clipboard.writeText(value).then(() => setCopied(true))
      .catch(() => setError('Could not copy. Press and hold the text to copy it.'));
  };

  const copyCaption = (value) => { setError(''); writeClipboard(value); };

  // A comment is checked against the 7-day reuse rule BEFORE it is copied, so nobody posts a
  // repeat on Instagram and only learns at Done.
  const copyComment = () => {
    setBusy(true); setError(''); setCopied(false);
    axios.post(`${API_URL}/engage/today/cards/${data.card.id}/check-comment`, { text })
      .then((r) => {
        if (!r.data || !r.data.reusable) { setError(REUSED); return undefined; }
        return writeClipboard(text);
      })
      .catch((e) => setError(e?.response?.data?.detail ? plain(e.response.data.detail) : 'Could not check the wording. Try again.'))
      .finally(() => setBusy(false));
  };

  if (!data) return <div className="et-page">{error || 'Loading…'}</div>;
  const c = data.card;

  let body;
  if (c) {
    body = (
      <div className="et-card">
        {c.kind === 'post' ? (
          <>
            <h2>Today's post: {c.school_name}</h2>
            <pre className="et-caption">{c.caption}</pre>
            <button type="button" className="et-button" data-testid="engage_today_copy" onClick={() => copyCaption(c.caption)}>
              {copied ? 'Copied' : 'Copy caption'}
            </button>
            {c.removed_mentions && c.removed_mentions.length > 0 && (c.buffer_state === 'ok' || c.buffer_maybe_queued) && (
              <p className="et-note" role="alert">
                Remove {c.removed_mentions.map((h) => `@${h}`).join(', ')} from the Buffer draft before approving.
              </p>
            )}
            {c.buffer_state === 'ok' && (
              <a className="et-primary" href={c.buffer_url} target="_blank" rel="noopener noreferrer">Review in Buffer</a>
            )}
            {c.buffer_state === 'error' && (c.buffer_maybe_queued
              ? <p className="et-note">Check the Buffer queue before posting by hand.</p>
              : <p className="et-note">Buffer is not reachable. Copy the caption and post it by hand.</p>)}
          </>
        ) : (
          <>
            <p className="et-kind">{c.kind === 'follow' ? 'Follow' : 'Comment on'}</p>
            <h2>{accountName(c)}</h2>
            <p className="et-handle">@{c.handle}</p>
            {schoolLine(c.school) && <p className="et-school">{schoolLine(c.school)}</p>}
            <p className="et-role">{c.role_label}</p>
            {c.why && <p className="et-why">{c.why}</p>}
            {c.profile_summary && <p className="et-why">{c.profile_summary}</p>}
            <a className="et-primary" data-testid="engage_today_open_profile" href={c.profile_url}
               target="_blank" rel="noopener noreferrer">Open profile</a>
            {c.kind === 'comment' && (
              <div className="et-comment">
                <p>Pick one of their recent posts. Use an idea below or write your own.</p>
                {c.drafts.map((d) => (
                  <button type="button" key={d} className="et-draft"
                          onClick={() => { setText(d); setCopied(false); }}>{d}</button>
                ))}
                <textarea data-testid="engage_today_comment_text" value={text} maxLength={300} rows={3}
                          onChange={(e) => { setText(e.target.value); setCopied(false); }} aria-label="Your comment" />
                <button type="button" className="et-button" data-testid="engage_today_copy" onClick={copyComment}
                        disabled={busy || !text.trim()}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}
          </>
        )}
        <div className="et-actions">
          <button type="button" className="et-done" data-testid="engage_today_done" disabled={busy} onClick={() => finish('done')}>Done</button>
          <button type="button" data-testid="engage_today_skip" disabled={busy} onClick={() => finish('skip')}>Skip</button>
          {c.kind !== 'post' && <>
            <button type="button" data-testid="engage_today_missing" disabled={busy} onClick={() => finish('missing')}>Doesn't exist</button>
            <button type="button" data-testid="engage_today_stop" disabled={busy} onClick={() => finish('stop')}>They asked us to stop</button>
          </>}
        </div>
      </div>
    );
  } else if (data.wait_seconds > 0) {
    body = (<div className="et-card"><p>Take a short break.</p>
      <p className="et-countdown" role="timer" data-testid="engage_today_countdown">
        {remaining > 0 ? `Next task in ${remaining} ${remaining === 1 ? 'second' : 'seconds'}.` : 'Loading the next task…'}
      </p>
      <button type="button" className="et-button" data-testid="engage_today_check_again" onClick={load}>Check again</button></div>);
  } else if (data.opens_at && data.opens_slot === 'pm') {
    body = (<div className="et-card"><p>{data.finished > 0 ? 'The morning tasks are done. The afternoon tasks open at '
      : 'Your afternoon tasks open at '}{clock(data.opens_at)}.</p></div>);
  } else if (data.opens_at) {
    body = <div className="et-card"><p>Your morning tasks open at {clock(data.opens_at)}.</p></div>;
  } else if (data.built === false) {
    body = <div className="et-card"><p>Today's tasks are being prepared. Check back in a few minutes.</p></div>;
  } else if (data.total === 0) {
    body = <div className="et-card"><p>Nothing today. Thank you!</p></div>;
  } else {
    body = <div className="et-card"><p>You're done for today. Thank you!</p></div>;
  }

  return (
    <div className="et-page">
      <p className="et-progress">{data.finished} of {data.total} done</p>
      {error && <p className="et-error" role="alert">{error}</p>}
      {body}
    </div>
  );
}
